/**
 * severity-provenance.ts — "why is this severity?" copy for the Hybrid drift
 * queue's severity chip (WO-5).
 *
 * TCM drift rows store the effective severity as a cache of MAX(static seed,
 * OPA policy hit, AI risk read) — see `TcmDriftEntity.Severity` and its three
 * signal columns. This module turns those signals into a customer-voiced
 * tooltip without naming any internal collector/engine ("OPA", "static",
 * "AI" never appear as jargon):
 *   - StaticSeverity -> "the baseline rating"
 *   - OpaSeverity    -> "the policy assessment"
 *   - AiRiskScore    -> "Guardian's risk read" (mapped to a severity band via
 *                        the same thresholds as the backend's
 *                        `DriftPolicyMappingService.MapRiskScoreToSeverity`:
 *                        >=85 Critical, >=60 High, >=30 Medium, else Low)
 *
 * Pure function, no DOM — the component test harness here can't run effects,
 * and the string-building is exactly the kind of branching that deserves
 * direct unit coverage (see `drift-decision-copy.ts` for the same pattern).
 */

export type ProvenanceSeverityBand = 'Critical' | 'High' | 'Medium' | 'Low'

const SEVERITY_RANK: Record<ProvenanceSeverityBand, number> = {
  Critical: 4,
  High: 3,
  Medium: 2,
  Low: 1,
}

function normalizeBand(value: string | null | undefined): ProvenanceSeverityBand | null {
  if (!value) return null
  switch (value.toLowerCase()) {
    case 'critical':
      return 'Critical'
    case 'high':
      return 'High'
    case 'medium':
      return 'Medium'
    case 'low':
      return 'Low'
    default:
      return null
  }
}

/** Mirrors `DriftPolicyMappingService.MapRiskScoreToSeverity`, scaled from
 *  the backend's 0-1 double thresholds to `AiRiskScore`'s 0-100 int. */
function aiRiskScoreToBand(score: number): ProvenanceSeverityBand {
  if (score >= 85) return 'Critical'
  if (score >= 60) return 'High'
  if (score >= 30) return 'Medium'
  return 'Low'
}

interface ProvenanceSignal {
  band: ProvenanceSeverityBand
  /** Used when this signal is (one of) the winner(s): "Rated High by {leadPhrase}." */
  leadPhrase: string
  /** Used when this signal is context, not the winner: "{contextLabel}: {band}." */
  contextLabel: string
}

export interface SeverityProvenanceInput {
  /** The row's displayed (effective) severity — `TcmDriftSummary.severity`. */
  effectiveSeverity: string | null
  staticSeverity: string | null
  opaSeverity: string | null
  aiRiskScore: number | null
}

function joinWithAnd(items: string[]): string {
  if (items.length === 1) return items[0]
  if (items.length === 2) return `${items[0]} and ${items[1]}`
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`
}

/**
 * Composes the severity chip's tooltip, or `null` when there's nothing to
 * explain (no effective severity, or no provenance signals at all — e.g. a
 * pre-WO-5 row that hasn't been backfilled). Callers should skip rendering a
 * `title`/tooltip entirely on `null` rather than show an empty one.
 */
export function composeSeverityProvenance(input: SeverityProvenanceInput): string | null {
  const effective = normalizeBand(input.effectiveSeverity)
  if (!effective) return null

  const signals: ProvenanceSignal[] = []
  if (typeof input.aiRiskScore === 'number') {
    signals.push({
      band: aiRiskScoreToBand(input.aiRiskScore),
      leadPhrase: "Guardian's risk read",
      contextLabel: 'Risk read',
    })
  }
  const opaBand = normalizeBand(input.opaSeverity)
  if (opaBand) {
    signals.push({ band: opaBand, leadPhrase: 'the policy assessment', contextLabel: 'Policy assessment' })
  }
  const staticBand = normalizeBand(input.staticSeverity)
  if (staticBand) {
    signals.push({ band: staticBand, leadPhrase: 'the baseline rating', contextLabel: 'Baseline rating' })
  }

  if (signals.length === 0) return null

  const maxRank = Math.max(...signals.map(s => SEVERITY_RANK[s.band]))
  const winners = signals.filter(s => SEVERITY_RANK[s.band] === maxRank)
  const others = signals.filter(s => SEVERITY_RANK[s.band] !== maxRank)

  // Normally the highest present signal IS the effective severity (the
  // backend cache is a pure MAX once SeverityComputedAt is stamped). If it
  // isn't — a legacy row whose cached Severity floors above every live
  // signal (see TcmDriftEntity.SeverityComputedAt) — say so honestly rather
  // than crediting a signal that didn't actually set it.
  const signalsExplainEffective = maxRank === SEVERITY_RANK[effective]
  const lead = signalsExplainEffective
    ? `Rated ${effective} by ${joinWithAnd(winners.map(w => w.leadPhrase))}.`
    : `Rated ${effective} from an earlier assessment. Current signals read lower.`

  const contextSignals = signalsExplainEffective ? others : signals
  const contextSentences = contextSignals.map(s => `${s.contextLabel}: ${s.band}.`)

  return [lead, ...contextSentences].join(' ')
}
